// PRD.305 follow-up — Form purpose vocabulary.
//
// Dependency-free (no workspace-package imports) so it runs under the node:test
// harness; `forms.ts` re-exports it, so the public import surface is unchanged.

/**
 * A form's workflow purpose.
 *
 * PERSISTENCE NOTE: `visit_form` is the DEFAULT and is stored as NULL — the
 * builder maps it (`purpose === 'visit_form' ? null : purpose`). Only the
 * non-default purposes occupy a stored value, which is why adding one is
 * additive and cannot disturb existing rows.
 *
 * `registration` is singular per org (`qActiveFormByPurpose` takes LIMIT 1).
 * `lab_work` is deliberately NOT — a clinic publishes many laboratory result
 * forms, so it is queried by assigned service instead (see
 * `qLabWorkFormsForService`).
 */
export type FormPurpose = 'visit_form' | 'registration' | 'lab_work';

export const FORM_PURPOSE_LABELS: Record<FormPurpose, string> = {
  visit_form: 'Visit Form',
  registration: 'Registration',
  lab_work: 'Lab Work',
};

/** Purposes offered by the builder, in display order. */
export const FORM_PURPOSES: readonly FormPurpose[] = [
  'visit_form',
  'registration',
  'lab_work',
] as const;

/** The stored value for a purpose — `visit_form` persists as NULL. */
export function formPurposeToStored(purpose: FormPurpose): string | null {
  return purpose === 'visit_form' ? null : purpose;
}

/** Read a stored value back. NULL / unknown means the default, Visit Form. */
export function formPurposeFromStored(stored: string | null | undefined): FormPurpose {
  return stored === 'registration' || stored === 'lab_work' ? stored : 'visit_form';
}

/** Whether a form captures a structured laboratory result. */
export function isLabWorkForm(form: { purpose?: string | null }): boolean {
  return formPurposeFromStored(form.purpose) === 'lab_work';
}
